import React from 'react';
import { Box, Grid, Typography, useTheme, alpha, IconButton } from '@mui/material';
import { Plus } from 'lucide-react';
import dayjs from 'dayjs';
import { CalendarEvent } from './CalendarEvent';
import { CalendarDay } from './types';

interface CalendarGridProps {
  currentDate: dayjs.Dayjs;
  days: CalendarDay[];
  onAddEvent?: (date: dayjs.Dayjs) => void;
  isMobile?: boolean;
}

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  days,
  onAddEvent,
  isMobile,
}) => {
  const theme = useTheme();
  const today = dayjs();

  // Organize days into weeks
  const weeks: (CalendarDay | null)[][] = [];
  let currentWeek: (CalendarDay | null)[] = [];

  days.forEach((day, index) => {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  // Add remaining days to the last week if needed
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={0}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid 
              item 
              key={day}
              sx={{ width: 'calc(100% / 7)' }}
            >
              <Typography
                component="span"
                align="center"
                sx={{
                  display: 'block',
                  py: 1,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {isMobile ? day.substring(0, 1) : day}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        {weeks.map((week, weekIndex) => (
          <Box 
            key={weekIndex} 
            sx={{ 
              display: 'flex',
              width: '100%',
            }}
          >
            {week.map((day, dayIndex) => {
              if (!day) {
                return (
                  <Box 
                    key={`empty-${dayIndex}`} 
                    sx={{ 
                      p: 1, 
                      minHeight: { xs: 80, sm: 100 },
                      width: 'calc(100% / 7)',
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                    }} 
                  />
                );
              }

              const isToday = day.date.isSame(today, 'day');

              return (
                <Box
                  key={day.date.date()}
                  sx={{
                    p: 1,
                    minHeight: { xs: 80, sm: 100 },
                    width: 'calc(100% / 7)',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isToday ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 1,
                  }}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 600 : 400,
                        color: isToday ? 'primary.main' : 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {day.date.date()}
                    </Typography>
                    {onAddEvent && (
                      <IconButton 
                        size="small" 
                        onClick={() => onAddEvent(day.date)}
                        sx={{ 
                          width: { xs: 24, sm: 28 },
                          height: { xs: 24, sm: 28 },
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '.MuiBox-root:hover &': {
                            opacity: 1,
                          }
                        }}
                      >
                        <Plus size={14} />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.5,
                    maxHeight: { xs: 60, sm: 80 },
                    overflowY: 'auto',
                  }}>
                    {day.events.map((event, index) => (
                      <CalendarEvent 
                        key={index} 
                        event={event}
                        isMobile={isMobile}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
};